import { useEffect, useLayoutEffect, useRef, useState, type MouseEvent as ReactMouseEvent } from 'react';
import { createPortal } from 'react-dom';
import { BookmarkIcon, CheckIcon, FolderIcon, FolderPlusIcon } from '@heroicons/react/24/outline';
import { BookmarkIcon as BookmarkIconSolid } from '@heroicons/react/24/solid';
import { useUser } from '@clerk/clerk-react';
import Button from '../ui/Button';
import { useAuthModal } from '../auth/AuthModalContext';
import { useSavedItems } from '../../hooks/useSavedItems';
import {
  LOCAL_DEFAULT_FOLDER_ID,
  SavedItemType,
  placeholderDefaultFolder,
} from '../../types/savedItems';
import { MY_ORBCON_LABEL } from '../../hooks/useUserSidebar';
import { stripHtml } from '../../utils/htmlEntities';

interface SaveToMyOrbconButtonProps {
  type: SavedItemType;
  title: string;
  slug: string;
  postId?: number;
  hubId?: string;
  programId?: string;
  variant?: 'detail' | 'icon';
  align?: 'center' | 'end';
}

export default function SaveToMyOrbconButton({
  type,
  title,
  slug,
  postId,
  hubId,
  programId,
  variant = 'detail',
  align = 'center',
}: SaveToMyOrbconButtonProps) {
  const { isSignedIn, isLoaded } = useUser();
  const { openAuthModal } = useAuthModal();
  const { folders, saveItem, createFolder, unsaveItem, findItem, refresh } = useSavedItems();
  const lookupId = type === 'RESOURCE' ? postId : type === 'HUB' ? hubId : programId;
  const saved = lookupId != null ? findItem(type, lookupId) : undefined;

  const [open, setOpen] = useState(false);
  const [naming, setNaming] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [creatingFolder, setCreatingFolder] = useState(false);
  const [savingFolderId, setSavingFolderId] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [menuPos, setMenuPos] = useState({ top: 0, left: 0 });

  const buttonRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const nameInputRef = useRef<HTMLInputElement>(null);
  const namingRef = useRef(false);

  const visibleFolders = folders.length > 0 ? folders : [placeholderDefaultFolder()];
  const savedFolder = saved
    ? visibleFolders.find((folder) => folder.id === saved.folderId)
    : undefined;
  const busy = savingFolderId != null || creatingFolder;
  namingRef.current = naming;

  useEffect(() => {
    if (!open) return;
    setError(null);
    setNaming(false);
    setNewFolderName('');
    void refresh();
    // Refresh once per open so Clerk getToken identity changes do not loop.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open]);

  useEffect(() => {
    if (!naming) return;
    nameInputRef.current?.focus();
  }, [naming]);

  useLayoutEffect(() => {
    if (!open || !buttonRef.current) return undefined;
    const update = () => {
      const rect = buttonRef.current?.getBoundingClientRect();
      if (!rect) return;
      const width = 288;
      const left = Math.min(Math.max(8, rect.left), window.innerWidth - width - 8);
      const top = Math.min(rect.bottom + 8, window.innerHeight - 8);
      setMenuPos({ top, left });
    };
    update();
    window.addEventListener('resize', update);
    window.addEventListener('scroll', update, true);
    return () => {
      window.removeEventListener('resize', update);
      window.removeEventListener('scroll', update, true);
    };
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    const onPointerDown = (event: globalThis.MouseEvent) => {
      const target = event.target as Node;
      if (buttonRef.current?.contains(target) || menuRef.current?.contains(target)) return;
      setOpen(false);
    };
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') return;
      if (namingRef.current) {
        setNaming(false);
        setNewFolderName('');
        return;
      }
      setOpen(false);
    };
    document.addEventListener('mousedown', onPointerDown);
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('mousedown', onPointerDown);
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [open]);

  const handleTrigger = (event: ReactMouseEvent) => {
    event.preventDefault();
    event.stopPropagation();
    if (!isLoaded) return;
    if (!isSignedIn) {
      openAuthModal('sign-in');
      return;
    }
    setOpen((current) => !current);
  };

  const saveToFolder = async (folderId?: string) => {
    const targetId = folderId || LOCAL_DEFAULT_FOLDER_ID;
    const folder = visibleFolders.find((entry) => entry.id === targetId);
    const alreadyHere = Boolean(
      saved &&
        (saved.folderId === folderId || (folder?.isDefault && savedFolder?.isDefault))
    );
    if (alreadyHere) {
      setOpen(false);
      return;
    }
    try {
      setSavingFolderId(targetId);
      setError(null);
      await saveItem({
        type,
        title: stripHtml(title),
        slug,
        postId,
        hubId,
        programId,
        folderId: folderId === LOCAL_DEFAULT_FOLDER_ID ? undefined : folderId,
      });
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to save');
    } finally {
      setSavingFolderId(null);
    }
  };

  const handleCreateFolder = async () => {
    const name = newFolderName.trim();
    if (!name) return;
    try {
      setCreatingFolder(true);
      setError(null);
      const folder = await createFolder(name);
      setNewFolderName('');
      setNaming(false);
      await saveToFolder(folder.id);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create folder');
    } finally {
      setCreatingFolder(false);
    }
  };

  const handleRemove = async () => {
    if (!saved) return;
    try {
      setSavingFolderId(saved.folderId);
      setError(null);
      await unsaveItem(saved.id);
      setOpen(false);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to remove');
    } finally {
      setSavingFolderId(null);
    }
  };

  const label = saved ? `Saved to ${MY_ORBCON_LABEL}` : `Add to ${MY_ORBCON_LABEL}`;
  const mark = saved ? (
    <BookmarkIconSolid className="h-5 w-5 flex-shrink-0 text-accent" />
  ) : (
    <BookmarkIcon className="h-5 w-5 flex-shrink-0 text-accent" />
  );

  return (
    <>
      <button
        ref={buttonRef}
        type="button"
        onClick={handleTrigger}
        className={
          variant === 'icon'
            ? `group min-h-[44px] min-w-[44px] inline-flex flex-shrink-0 text-accent focus-ring ${
                align === 'end' ? 'items-start justify-end' : 'items-center justify-center'
              }`
            : 'inline-flex items-center gap-2 min-h-[44px] px-3 text-sm font-medium text-accent hover:bg-accent/5 rounded-md focus-ring'
        }
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label={label}
      >
        {variant === 'icon' ? (
          <span className="inline-flex rounded-md p-2 group-hover:bg-accent/5">{mark}</span>
        ) : (
          mark
        )}
        {variant === 'detail' ? <span>{label}</span> : null}
      </button>

      {open &&
        createPortal(
          <div
            ref={menuRef}
            role="dialog"
            aria-label={`Save to ${MY_ORBCON_LABEL}`}
            className="fixed z-[70] w-72 max-w-[calc(100vw-16px)] bg-white border border-gray-200 rounded-md shadow-lg p-4"
            style={{ top: menuPos.top, left: menuPos.left }}
            onClick={(event) => event.stopPropagation()}
          >
            <p className="text-sm font-semibold text-gray-900 mb-3">Save to a folder</p>
            <ul className="space-y-1 mb-3 max-h-48 overflow-y-auto">
              {visibleFolders.map((folder) => {
                const isCurrent = Boolean(
                  saved &&
                    (saved.folderId === folder.id ||
                      (folder.isDefault && savedFolder?.isDefault))
                );
                const isSaving = savingFolderId === folder.id;
                return (
                  <li key={folder.id || LOCAL_DEFAULT_FOLDER_ID}>
                    <button
                      type="button"
                      disabled={busy}
                      onClick={() => void saveToFolder(folder.id)}
                      className={`flex items-center gap-2 w-full min-h-[44px] px-2 rounded-md text-left text-sm focus-ring ${
                        isCurrent
                          ? 'bg-accent/5 text-gray-900'
                          : 'text-gray-800 hover:bg-gray-50'
                      } disabled:opacity-50`}
                      aria-current={isCurrent ? 'true' : undefined}
                    >
                      <FolderIcon className="h-5 w-5 flex-shrink-0 text-accent" />
                      <span className="truncate flex-1">{folder.name}</span>
                      {isCurrent ? (
                        <CheckIcon className="h-4 w-4 flex-shrink-0 text-accent" aria-hidden />
                      ) : isSaving ? (
                        <span className="text-xs text-gray-500">Saving…</span>
                      ) : null}
                    </button>
                  </li>
                );
              })}
              <li>
                {naming ? (
                  <div className="flex items-center gap-2 min-h-[44px] px-2 rounded-md text-sm text-gray-800">
                    <FolderPlusIcon className="h-5 w-5 flex-shrink-0 text-accent" />
                    <input
                      ref={nameInputRef}
                      type="text"
                      value={newFolderName}
                      onChange={(event) => setNewFolderName(event.target.value)}
                      placeholder="Folder name"
                      maxLength={60}
                      disabled={busy}
                      aria-label="New folder name"
                      className="flex-1 min-w-0 bg-transparent border-0 p-0 text-sm text-gray-800 placeholder:text-gray-500 focus:ring-0"
                      onKeyDown={(event) => {
                        if (event.key === 'Enter') {
                          event.preventDefault();
                          void handleCreateFolder();
                        }
                        if (event.key === 'Escape') {
                          event.preventDefault();
                          event.stopPropagation();
                          setNaming(false);
                          setNewFolderName('');
                        }
                      }}
                    />
                  </div>
                ) : (
                  <button
                    type="button"
                    disabled={busy}
                    onClick={() => {
                      setError(null);
                      setNaming(true);
                    }}
                    className="flex items-center gap-2 w-full min-h-[44px] px-2 rounded-md text-left text-sm text-gray-800 hover:bg-gray-50 focus-ring disabled:opacity-50"
                  >
                    <FolderPlusIcon className="h-5 w-5 flex-shrink-0 text-accent" />
                    <span className="truncate flex-1">New folder</span>
                  </button>
                )}
              </li>
            </ul>

            {error ? <p className="text-sm text-error mb-3">{error}</p> : null}

            {saved ? (
              <Button type="button" variant="tertiary" disabled={busy} onClick={() => void handleRemove()}>
                Remove from {MY_ORBCON_LABEL}
              </Button>
            ) : null}
          </div>,
          document.body
        )}
    </>
  );
}
